import React, { useMemo, useCallback } from 'react';
import { noop } from 'lodash';
import { Button, Tooltip } from '@panwds/react-ui';
import { useFilters } from '@panwds/react-filters';
import { classNames as cn } from '@panwds/tailwindcss';
import { RedoIcon } from '@panwds/icons';
import { MAP_STATUS_COLORS, MAP_PLANE_TYPES } from '../../../common-resources/utils';
import dayjs, { getTimeZoneAbbreviation, getLocalBrowserTimeZone } from '../../../shared/utils/date';
import mapCSS from './Map.module.scss';
import commonStyles from '../../../scss/StatusPageCommon.module.scss';

const FILTER_CONTEXT_ID = 'StatusPageFilterBar';
const DATE_TIME_FORMAT_LONG = 'ddd, MMM D @h:mm A';

const STATUS_PILLS = [
    { key: 'available',  label: 'Operational', filterValue: 'available'  },
    { key: 'degraded',   label: 'Degraded',    filterValue: 'degraded'   },
    { key: 'disruption', label: 'Disrupted',   filterValue: 'disruption' },
];

// The all-selected default: all three status values explicitly chosen.
// This is the canonical value for "no filter applied" — stored explicitly so
// pills show as active on page load without any special-casing.
export const STATUS_TYPE_ALL_SELECTED = 'available,degraded,disruption';

// Parse statusType (comma-separated string) into a Set for O(1) lookups.
// 'all' and the explicit all-selected string both resolve to the full set.
export const parseStatusTypeFilter = (statusType) => {
    if (!statusType || statusType === 'all') {
        return new Set(['available', 'degraded', 'disruption']);
    }
    return new Set(statusType.split(',').filter(Boolean));
};

const MapStatusBar = props => {
    const {
        filteredMapData = [],
        allMapData,
        planeType = 'all',
        statusType = 'all',
        incidentsUpdatedTime,
        handleRefresh = noop,
    } = props;

    const { setFilters: setPageFilters } = useFilters(FILTER_CONTEXT_ID);

    const handleRefreshClick = useCallback(() => {
        handleRefresh();
    }, [handleRefresh]);

    // Use allMapData (plane-filtered only) for stable counts so pill numbers
    // don't change when the status filter is toggled.
    const countSource = allMapData || filteredMapData;

    const {
        operationalCount,
        degradedCount,
        disruptionCount,
        updatedTime,
        updatedTimeHover,
    } = useMemo(() => {
        let disruptionCount = 0;
        let operationalCount = 0;
        let degradedCount = 0;
        countSource.features.forEach(feature => {
            const markerProps = feature.properties || {};

            if (markerProps.status === 'disruption') {
                disruptionCount += 1;
            } else if (markerProps.status === 'degraded') {
                degradedCount += 1;
            } else {
                operationalCount += 1;
            }
        });

        const tz = getLocalBrowserTimeZone();
        const tzDisplayLong = getTimeZoneAbbreviation(tz, 'long');
        const tmpDate = incidentsUpdatedTime || new Date().valueOf();
        // Shown in UTC like every other time on the page; the local equivalent is on hover.
        const dateStr = dayjs.utc(tmpDate).format(DATE_TIME_FORMAT_LONG) + ' UTC';
        const dateLongStr = `Your local time: ${dayjs(tmpDate).format(DATE_TIME_FORMAT_LONG)} ${tzDisplayLong}`;

        return {
            operationalCount,
            degradedCount,
            disruptionCount,
            updatedTime: dateStr,
            updatedTimeHover: dateLongStr,
        };
    }, [countSource, incidentsUpdatedTime]);

    const counts = {
        available: operationalCount,
        degraded: degradedCount,
        disruption: disruptionCount,
    };

    const planeTypeLabel = useMemo(() => {
        return MAP_PLANE_TYPES?.[planeType]?.locationLabel || 'Locations';
    }, [planeType]);

    const activeSet = useMemo(() => parseStatusTypeFilter(statusType), [statusType]);
    const hasActiveFilter = activeSet.size < 3;

    const handlePillClick = useCallback((filterValue) => {
        const next = new Set(activeSet);
        if (next.has(filterValue)) {
            next.delete(filterValue);
        } else {
            next.add(filterValue);
        }
        // If all three are selected, store as the canonical all-selected value.
        const nextValue = next.size === 3 ? STATUS_TYPE_ALL_SELECTED : [...next].join(',');
        setPageFilters({ statusType: nextValue });
    }, [setPageFilters, activeSet]);

    return (
        <div
            className={cn(
                mapCSS.mapStatusBarRoot,
                'ssmutw-py-0.5',
            )}
        >
            <div className={cn("ssmutw-flex ssmutw-text-left ssmutw-items-center", commonStyles.contentWidth)}>
                <div className="ssmutw-flex-grow">
                    <div className="ssmutw-flex ssmutw-items-center ssmutw-gap-1">
                        <span style={{ marginRight: 4 }}>{planeTypeLabel} ({operationalCount + degradedCount + disruptionCount}):</span>
                        {STATUS_PILLS.map(({ key, label, filterValue }) => {
                            const isActive = activeSet.has(filterValue);
                            const isDimmed = hasActiveFilter && !isActive;
                            return (
                                <button
                                    key={key}
                                    className={cn(
                                        mapCSS.statusPillBtn,
                                        isActive && mapCSS.statusPillActive,
                                        isDimmed && mapCSS.statusPillDimmed,
                                    )}
                                    style={{ color: MAP_STATUS_COLORS[key] }}
                                    onClick={() => handlePillClick(filterValue)}
                                    title={isActive ? `Remove ${label} filter` : `Filter by ${label}`}
                                >
                                    {label}
                                    <span style={{ opacity: 0.75 }}>({counts[key]})</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="ssmutw-text-right ssmutw-flex ssmutw-space-x-2 ssmutw-items-center">
                    <Tooltip label={updatedTimeHover}>
                        <span style={{ fontWeight: 200, fontSize: 12 }}>
                            Last updated: {updatedTime}
                        </span>
                    </Tooltip>
                    <Tooltip label="Refresh">
                        <Button
                            aria-label="Refresh"
                            icon={<RedoIcon size="xs" />}
                            appearance="tertiary-clear"
                            addClassName="ssmutw-inline"
                            onClick={handleRefreshClick}
                        />
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

export default React.memo(MapStatusBar);
